import { useState } from 'react';
import { Upload, CheckCircle, Loader2, X } from 'lucide-react';
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { useLanguage } from '../../contexts/LanguageContext';
import { apiService } from '../../services/api';
import { toast } from 'sonner';

interface FileUploadProps {
  label: string;
  accept: string;
  required?: boolean;
  applicationId?: number; // Optional - only provided after application is created
  documentType?: string; // Maps to document type for the backend
  onUploadComplete?: (fileData: { id: number; documentUrl: string; fileName: string; fileSize: number; documentType: string }) => void;
  onFileSelect?: (file: File | null) => void; // For storing file temporarily before application is created
}

export function FileUpload({ 
  label, 
  accept, 
  required = false, 
  applicationId, 
  documentType, 
  onUploadComplete, 
  onFileSelect 
}: FileUploadProps) {
  const { t } = useLanguage();
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedData, setUploadedData] = useState<{ id: number; documentUrl: string; fileName: string; fileSize: number; documentType: string } | null>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (fileOrEvent: File | React.ChangeEvent<HTMLInputElement>) => {
    let file: File | null = null;
    
    if (fileOrEvent instanceof File) {
      file = fileOrEvent;
    } else {
      const files = fileOrEvent.target.files;
      if (files && files.length > 0) {
        file = files[0];
      }
    }

    if (file) {
      setFileName(file.name);
      setSelectedFile(file);
      setUploadedData(null); // Clear previous upload data
      
      // Notify parent component about file selection
      if (onFileSelect) {
        onFileSelect(file);
      }

      // If applicationId is available, upload immediately
      if (applicationId && documentType) {
        uploadFile(file);
      }
    }
  };

  const uploadFile = async (file: File) => {
    if (!applicationId || !documentType) {
      toast.error('Cannot upload file: Application not created yet');
      return;
    }

    setIsUploading(true);
    try {
      const uploadResult = await apiService.uploadApplicationDocument(
        applicationId,
        file,
        documentType
      );
      
      setUploadedData(uploadResult);
      toast.success(`${label} uploaded successfully`);
      
      if (onUploadComplete) {
        onUploadComplete(uploadResult);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(`Failed to upload ${label}`);
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = () => {
    setFileName(null);
    setSelectedFile(null);
    setUploadedData(null);
    
    if (onFileSelect) {
      onFileSelect(null);
    }
  };

  // Allow manual upload if file is selected but not uploaded yet
  const canUpload = selectedFile && applicationId && documentType && !uploadedData && !isUploading;
  const isUploaded = uploadedData !== null;

  return (
    <div>
      <Label className="block mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
          dragOver 
            ? 'border-[#f91bdc] bg-[#f91bdc]/5' 
            : isUploaded
              ? 'border-green-500 bg-green-50'
              : fileName 
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept={accept}
          className="hidden"
          id={`file-${label.replace(/\s+/g, '-').toLowerCase()}`}
          onChange={handleFileSelect}
        />
        
        {fileName ? (
          <div className="space-y-3">
            <div className={`flex items-center justify-center space-x-2 ${
              isUploaded ? 'text-green-600' : isUploading ? 'text-blue-600' : 'text-gray-600'
            }`}>
              {isUploading ? (
                <Loader2 size={20} className="animate-spin" />
              ) : isUploaded ? (
                <CheckCircle size={20} />
              ) : (
                <Upload size={20} />
              )}
              <span className="font-medium">{fileName}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={removeFile}
                className="h-6 w-6 p-0 hover:bg-red-100"
              >
                <X size={14} />
              </Button>
            </div>
            
            {isUploading && (
              <p className="text-sm text-blue-600">Uploading...</p>
            )}
            
            {isUploaded && (
              <p className="text-sm text-green-600">✓ Uploaded successfully</p>
            )}
            
            {canUpload && (
              <div className="space-y-2">
                <p className="text-sm text-orange-600">⚠️ File selected but not uploaded yet</p>
                <Button
                  type="button"
                  onClick={() => selectedFile && uploadFile(selectedFile)}
                  className="bg-[#f91bdc] hover:bg-[#e015c9] text-white text-sm px-4 py-2"
                >
                  Upload Now
                </Button>
              </div>
            )}
            
            {!applicationId && (
              <p className="text-xs text-gray-500">File will be uploaded after submitting application</p>
            )}
          </div>
        ) : (
          <label 
            htmlFor={`file-${label.replace(/\s+/g, '-').toLowerCase()}`}
            className="cursor-pointer block"
          >
            <Upload className="mx-auto text-gray-400 mb-2" size={32} />
            <p className="text-gray-600">
              <span className="text-[#f91bdc] font-medium">{t('buyingForm.clickToUpload')}</span> {t('buyingForm.dragAndDrop')}
            </p>
            <p className="text-xs text-gray-400 mt-1">{t('buyingForm.fileSize')}</p>
          </label>
        )}
      </div>
    </div>
  );
}